// Invoice-style document for sales, purchases, quotations, return orders and
// deliveries — React port of helpers/OrderViewComponent.vue.

import { Fragment, type ReactNode } from 'react';

import { formatDate, formatDateTime, hasTranslation, t } from '../../i18n';
import { asArray, asRecord, extraAttributeRows, gatewayLabel, makeFormatters, metaString, partyLines, str, toNumber, type AnyRecord } from './format';
import { useStoreSettings, type StoreSettings } from './storeSettings';

export type OrderDocumentKind = 'sale' | 'purchase' | 'quotation' | 'return_order' | 'delivery';

type Options = {
  heading: string;
  field: 'price' | 'cost';
  to: 'customer' | 'supplier' | null;
  toText: string;
  onlyQty: boolean;
  payment: boolean;
  summary: boolean;
  extra: boolean;
  delivery: boolean;
};

export function documentOptions(kind: OrderDocumentKind, record: AnyRecord): Options {
  const base: Options = { heading: t(`doc.kind.${kind}`), field: 'price', to: 'customer', toText: t('doc.billTo'), onlyQty: false, payment: true, summary: true, extra: true, delivery: false };
  switch (kind) {
    case 'purchase':
      return { ...base, field: 'cost', to: 'supplier', toText: t('doc.orderTo') };
    case 'quotation':
      return { ...base, summary: false, toText: t('doc.quoteTo') };
    case 'return_order': {
      const sale = record.type !== 'purchase';
      return {
        ...base,
        heading: `${t('doc.kind.return_order')} (${t(sale ? 'doc.kind.sale' : 'doc.kind.purchase')})`,
        field: sale ? 'price' : 'cost',
        to: sale ? 'customer' : 'supplier',
        toText: sale ? t('doc.from') : t('doc.to')
      };
    }
    case 'delivery':
      return { ...base, to: null, toText: '', onlyQty: true, payment: false, extra: false, delivery: true, field: 'cost' };
    default:
      return base;
  }
}

function plain(html: unknown): string {
  return str(html)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .trim();
}

export function DocumentHeader({ record, settings, reference }: { record: AnyRecord; settings: StoreSettings; reference: unknown }) {
  const location = asRecord(record.location);
  const logo = str(location?.logo) || str(settings.default_logo);
  return (
    <div className="vd-header">
      <div className="vd-brand">
        {logo ? <img className="vd-logo" src={logo} alt="" /> : null}
        <ul>
          {settings.name ? (
            <li>
              <strong>
                {str(settings.name)}
                {settings.short_name ? ` (${str(settings.short_name)})` : ''}
              </strong>
            </li>
          ) : null}
          {settings.company ? (
            <li>
              <strong>{str(settings.company)}</strong>
            </li>
          ) : null}
          {settings.email || settings.phone ? (
            <li>
              {str(settings.email)}
              {settings.email && settings.phone ? <span className="vd-muted"> | </span> : null}
              {str(settings.phone)}
            </li>
          ) : null}
          {settings.address ? <li>{str(settings.address)}</li> : null}
        </ul>
      </div>
      {reference ? <div className="vd-ref-badge">{str(reference)}</div> : null}
    </div>
  );
}

export function PartyBlock({ title, party }: { title: string; party: AnyRecord | null }) {
  return (
    <div>
      <div className="vd-muted">{title}:</div>
      {party ? (
        <>
          <strong>{str(party.name ?? party.label)}</strong>
          {party.company ? <span> ({str(party.company)})</span> : null}
          {partyLines(party).map((line) => (
            <div key={line}>{line}</div>
          ))}
        </>
      ) : null}
    </div>
  );
}

export function ExtraAttributesTable({ record }: { record: AnyRecord }) {
  const rows = extraAttributeRows(record);
  if (!rows.length) return null;
  return (
    <table className="vd-table">
      <tbody>
        {rows.map((row) => (
          <tr key={row.label}>
            <td style={{ width: '35%' }}>{row.label}</td>
            <td>{row.value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function AttachmentLinks({ record }: { record: AnyRecord }) {
  const attachments = asArray(record.attachments);
  if (!attachments.length) return null;
  return (
    <div className="vd-attachments vd-no-print">
      <strong>{t('doc.attachments')}: </strong>
      {attachments.map((attachment, index) => {
        const href = str(attachment.original_url ?? attachment.url);
        const name = str(attachment.name ?? attachment.file_name ?? index + 1);
        return href ? (
          <a key={href + index} href={href} target="_blank" rel="noreferrer">
            {name}
          </a>
        ) : (
          <span key={name + index}>{name} </span>
        );
      })}
    </div>
  );
}

type TaxRow = { id: string; name: string; code: string; quantity: number; net: number; amount: number };

function taxSummary(record: AnyRecord, field: 'price' | 'cost'): TaxRow[] {
  const taxes: TaxRow[] = [];
  const add = (tax: AnyRecord, quantity: number, net: number) => {
    const amount = Math.round(((net * toNumber(tax.rate)) / 100) * 10000) / 10000;
    const existing = taxes.find((entry) => entry.id === str(tax.id));
    if (existing) {
      existing.amount += amount;
      existing.quantity += quantity;
      existing.net += net;
    } else {
      taxes.push({ id: str(tax.id), name: str(tax.name), code: str(tax.code), quantity, net, amount });
    }
  };
  asArray(record.items).forEach((item) => {
    asArray(item.taxes).forEach((tax) => add(tax, toNumber(item.quantity), toNumber(item[`net_${field}`]) * toNumber(item.quantity)));
  });
  asArray(record.taxes).forEach((tax) => add(tax, 0, toNumber(record.total)));
  return taxes;
}

export function OrderDocument({ record, kind, settings: settingsOverride }: { record: AnyRecord; kind: OrderDocumentKind; settings?: StoreSettings }) {
  const loaded = useStoreSettings();
  const settings = settingsOverride ?? loaded;
  const fmt = makeFormatters(settings);
  const options = documentOptions(kind, record);
  const { field, onlyQty } = options;
  const showTax = String(settings.show_tax) === '1';
  const showDiscount = options.payment && String(settings.show_discount) === '1';
  // Price columns (price, discount?, tax?, subtotal) and the footer label span.
  const priceCols = onlyQty ? 0 : 2 + (showDiscount ? 1 : 0) + (showTax ? 1 : 0);
  const cols = 1 + priceCols;
  const location = asRecord(record.location);
  const toParty = options.to ? asRecord(record[options.to]) : null;
  const items = asArray(record.items);
  const payments = asArray(record.payments);
  const paymentAmount = (payment: AnyRecord) => toNumber(asRecord(payment.pivot)?.amount ?? payment.amount);
  const totalPaid = payments.reduce((sum, payment) => sum + paymentAmount(payment), 0);
  const user = asRecord(record.user);
  const taxes = options.summary && String(settings.show_tax_summary) === '1' ? taxSummary(record, field) : [];

  const emptyCells = (count: number) => Array.from({ length: count }, (_, index) => <td key={`e${index}`} />);
  const priceCells = (values: { price?: ReactNode; discount?: ReactNode; tax?: ReactNode; total?: ReactNode }) =>
    onlyQty ? null : (
      <>
        <td className="vd-right">{values.price ?? ''}</td>
        {showDiscount ? <td className="vd-right">{values.discount ?? ''}</td> : null}
        {showTax ? <td className="vd-right">{values.tax ?? ''}</td> : null}
        <td className="vd-right">{values.total ?? ''}</td>
      </>
    );

  const itemName = (item: AnyRecord) => {
    const inner = asRecord(item.item);
    return str(inner?.name ?? item.name);
  };

  const renderItem = (item: AnyRecord, index: number) => {
    const inner = asRecord(item.item);
    const variations = asArray(item.variations);
    const portions = asArray(item.portions);
    const modifiers = asArray(item.modifier_options);
    const rows: ReactNode[] = [];
    if (variations.length) {
      rows.push(
        <tr key={`h${index}`}>
          <td>
            <strong>{itemName(item)}</strong>
          </td>
          <td />
          {emptyCells(priceCols)}
        </tr>
      );
      variations.forEach((variation, vi) => {
        const pivot = asRecord(variation.pivot) ?? {};
        rows.push(
          <tr key={`v${index}-${vi}`} className="vd-sub">
            <td>{metaString(variation.meta)}</td>
            <td className="vd-right">{fmt.qty(pivot.quantity)}</td>
            {priceCells({
              price: fmt.money(pivot[field]),
              discount: fmt.money(pivot.total_discount_amount),
              tax: fmt.money(pivot.total_tax_amount),
              total: fmt.money(pivot.total)
            })}
          </tr>
        );
      });
    } else if (portions.length) {
      rows.push(
        <tr key={`h${index}`}>
          <td>
            <strong>{itemName(item)}</strong>
          </td>
          <td />
          {emptyCells(priceCols)}
        </tr>
      );
      portions.forEach((portion, pi) => {
        const pivot = asRecord(portion.pivot) ?? {};
        const portionQty = toNumber(pivot.quantity);
        const price = showTax
          ? toNumber(pivot[`net_${field}`])
          : String(settings.show_discount) === '1'
            ? toNumber(pivot[field])
            : toNumber(pivot[field]) - toNumber(pivot.discount_amount) + toNumber(pivot.tax_amount);
        rows.push(
          <tr key={`p${index}-${pi}`} className="vd-bold">
            <td style={{ paddingLeft: 14 }}>{portion.name === 'regular' ? t('doc.regular') : str(portion.name)}</td>
            <td className="vd-right">{fmt.qty(portionQty)}</td>
            {priceCells({ price: fmt.money(price), discount: fmt.money(pivot.total_discount_amount), tax: fmt.money(pivot.total_tax_amount), total: fmt.money(pivot.total) })}
          </tr>
        );
        const subRow = (key: string, label: ReactNode, quantity: number) => (
          <tr key={key} className="vd-sub">
            <td>{label}</td>
            <td className="vd-right">{fmt.qty(quantity)}</td>
            {emptyCells(priceCols)}
          </tr>
        );
        asArray(portion.portion_items).forEach((entry, ei) => {
          rows.push(subRow(`pi${index}-${pi}-${ei}`, `${str(asRecord(entry.item)?.name)}${entry.meta ? ` (${metaString(entry.meta)})` : ''}`, toNumber(entry.quantity) * portionQty));
        });
        asArray(portion.essentials).forEach((entry, ei) => {
          rows.push(subRow(`pe${index}-${pi}-${ei}`, `${str(asRecord(entry.item)?.name)}${entry.meta ? ` (${metaString(entry.meta)})` : ''}`, toNumber(entry.quantity) * portionQty));
        });
        const chosen = asArray(pivot.choosables);
        asArray(portion.choosables).forEach((group, gi) => {
          asArray(group.items).forEach((groupItem, gii) => {
            if (!chosen.find((choice) => str(choice.id) === str(group.id) && str(choice.item_id) === str(groupItem.item_id))) return;
            rows.push(
              subRow(
                `pc${index}-${pi}-${gi}-${gii}`,
                <>
                  <strong>{str(group.name)}</strong> {str(asRecord(groupItem.item)?.name)}
                </>,
                toNumber(groupItem.quantity) * portionQty
              )
            );
          });
        });
      });
    } else {
      rows.push(
        <tr key={`i${index}`}>
          <td>
            {itemName(item)}
            {inner?.code || item.code ? ` [${str(inner?.code ?? item.code)}]` : ''}
            {inner?.alt_name ? <div className="vd-muted">{str(inner.alt_name)}</div> : null}
            {item.comment ? <div className="vd-muted">{str(item.comment)}</div> : null}
          </td>
          <td className="vd-right">{fmt.qty(item.quantity)}</td>
          {priceCells({
            price: fmt.money(showTax ? item[`net_${field}`] : item[`unit_${field}`] ?? item[field]),
            discount: fmt.money(item.total_discount_amount),
            tax: fmt.money(item.total_tax_amount),
            total: fmt.money(item.subtotal)
          })}
        </tr>
      );
    }
    modifiers.forEach((modifier, mi) => {
      const pivot = asRecord(modifier.pivot) ?? {};
      const price = showTax ? toNumber(pivot[field]) : toNumber(pivot[field]) - toNumber(pivot.discount_amount) + toNumber(pivot.tax_amount);
      rows.push(
        <tr key={`m${index}-${mi}`} className="vd-sub">
          <td>
            <strong>{str(asRecord(modifier.modifier)?.title)}</strong> {str(asRecord(modifier.item)?.name ?? modifier.name)}
          </td>
          <td className="vd-right">{fmt.qty(pivot.quantity)}</td>
          {priceCells({ price: fmt.money(price), discount: fmt.money(pivot.total_discount_amount), tax: fmt.money(pivot.total_tax_amount), total: fmt.money(pivot.total) })}
        </tr>
      );
    });
    return <Fragment key={str(item.id) || index}>{rows}</Fragment>;
  };

  const footerRow = (label: ReactNode, value: ReactNode, key: string) => (
    <tr key={key}>
      <th className="vd-left" colSpan={cols}>
        {label}
      </th>
      <th className="vd-right">{value}</th>
    </tr>
  );

  const notes = [plain(record.details), plain(location?.footer), plain(settings.footer)].filter(Boolean);

  return (
    <div className="vd-doc">
      <DocumentHeader record={record} settings={settings} reference={record.reference} />
      <h3 className="vd-heading">{options.heading}</h3>
      <div className="vd-row">
        <div>
          {record.date ? (
            <>
              {t('doc.date')}: <strong>{formatDate(record.date)}</strong>
            </>
          ) : null}
        </div>
        <div>
          {t('doc.reference')}: <strong>{str(record.reference)}</strong>
        </div>
      </div>
      {record.orderNumber || record.ticketNumber ? (
        <div className="vd-row">
          <div>
            {t('doc.orderNumber')}: <strong>{str(record.orderNumber)}</strong>
          </div>
          <div>
            {t('doc.ticketNumber')}: <strong>{str(record.ticketNumber)}</strong>
          </div>
        </div>
      ) : null}
      {toNumber(record.void) === 1 ? <div className="vd-alert error">{t('doc.voidText')}</div> : null}
      {toNumber(record.draft) === 1 ? <div className="vd-alert warning">{t('doc.draftText')}</div> : null}
      {record.recurring_sale_id ? <div className="vd-alert warning">{t('doc.recurringText', { x: str(record.recurring_sale_id) })}</div> : null}
      <div className="vd-parties">
        <PartyBlock title={t('doc.store')} party={location} />
        {options.to ? <PartyBlock title={options.toText} party={toParty} /> : null}
      </div>
      {options.extra ? <ExtraAttributesTable record={record} /> : null}
      {items.length ? (
        <table className="vd-table">
          <thead>
            <tr>
              <th className="vd-left">{t('doc.description')}</th>
              <th style={{ width: 90 }}>{t('doc.quantity')}</th>
              {onlyQty ? null : (
                <>
                  <th style={{ width: 100 }}>{field === 'cost' ? t('doc.cost') : t('doc.price')}</th>
                  {showDiscount ? <th style={{ width: 90 }}>{t('doc.discount')}</th> : null}
                  {showTax ? <th style={{ width: 90 }}>{t('doc.tax')}</th> : null}
                  <th style={{ width: 110 }}>{t('doc.subtotal')}</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>{items.map(renderItem)}</tbody>
          {onlyQty ? null : (
            <tfoot>
              <tr>
                <th className="vd-left" colSpan={cols - (showTax ? 1 : 0)}>
                  {t('doc.total')}
                </th>
                {showTax ? <th className="vd-right">{fmt.money(record.item_tax_amount)}</th> : null}
                <th className="vd-right">{fmt.money(record.total)}</th>
              </tr>
              {toNumber(record.order_tax_amount) > 0 ? footerRow(t('doc.orderTax'), fmt.money(record.order_tax_amount), 'ot') : null}
              {toNumber(record.order_discount_amount) > 0 ? footerRow(t('doc.orderDiscount'), fmt.money(record.order_discount_amount), 'od') : null}
              {!showTax && toNumber(record.total_tax_amount) > 0 ? footerRow(t('doc.totalTax'), fmt.money(record.total_tax_amount), 'tt') : null}
              {toNumber(record.shipping) > 0 ? footerRow(t('doc.shipping'), fmt.money(record.shipping), 'sh') : null}
              {toNumber(record.total) !== toNumber(record.grand_total) ? footerRow(t('doc.grandTotal'), fmt.money(record.grand_total), 'gt') : null}
              {options.payment ? (
                payments.length ? (
                  <>
                    {payments.map((payment, index) => (
                      <tr key={`pay${str(payment.id) || index}`}>
                        <td colSpan={cols}>
                          <strong>{t('doc.paymentSettlement')}</strong>
                          <br />({t('doc.date')}: {formatDate(payment.created_at ?? payment.date)}, {t('doc.reference')}: {str(payment.reference)}
                          {payment.gateway ? `, ${gatewayLabel(payment.gateway, t, hasTranslation)}` : ''})
                        </td>
                        <td className="vd-right vd-bold">{fmt.money(paymentAmount(payment))}</td>
                      </tr>
                    ))}
                    {footerRow(t('doc.balanceDue'), fmt.money(toNumber(record.grand_total) - totalPaid), 'bd')}
                  </>
                ) : (
                  <>
                    {footerRow(t('doc.paid'), fmt.money(0), 'pd')}
                    {footerRow(t('doc.balanceDue'), fmt.money(record.grand_total), 'bd')}
                  </>
                )
              ) : null}
            </tfoot>
          )}
        </table>
      ) : null}
      {taxes.length ? (
        <table className="vd-table">
          <thead>
            <tr>
              <th colSpan={5} className="vd-left">
                {t('doc.taxSummary')}
              </th>
            </tr>
            <tr>
              <th>{t('doc.name')}</th>
              <th style={{ width: 90 }}>{t('doc.code')}</th>
              <th style={{ width: 90 }}>{t('doc.qtyWeight')}</th>
              <th style={{ width: 140 }}>{t('doc.taxExAmount')}</th>
              <th style={{ width: 110 }}>{t('doc.taxAmount')}</th>
            </tr>
          </thead>
          <tbody>
            {taxes.map((tax) => (
              <tr key={tax.id}>
                <td>{tax.name}</td>
                <td className="vd-center">{tax.code}</td>
                <td className="vd-center">{fmt.qty(tax.quantity)}</td>
                <td className="vd-right">{fmt.money(tax.net)}</td>
                <td className="vd-right">{fmt.money(tax.amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th colSpan={4} className="vd-right">
                {t('doc.totalTax')}
              </th>
              <th className="vd-right">{fmt.money(record.total_tax_amount)}</th>
            </tr>
          </tfoot>
        </table>
      ) : null}
      {notes.length ? <div className="vd-box">{notes.join('\n\n')}</div> : null}
      <AttachmentLinks record={record} />
      {user ? (
        <div className="vd-row" style={{ borderBottom: 'none' }}>
          <div />
          <div>
            {t('doc.createdBy')}: <strong>{str(user.name)}</strong>
            <br />
            {t('doc.createdAt')}: <strong>{formatDateTime(record.created_at)}</strong>
          </div>
        </div>
      ) : null}
      {options.delivery ? (
        <>
          <div className="vd-row">
            <div>
              {t('doc.status')}: <strong>{record.status ? t(`orderActions.deliveryStatus.${str(record.status)}`) : ''}</strong>
            </div>
            <div>
              {t('doc.deliveredAt')}: <strong>{record.delivered_at ? formatDateTime(record.delivered_at) : ''}</strong>
            </div>
          </div>
          <div className="vd-row">
            <div>
              {t('doc.deliveredBy')}: <strong>{str(asRecord(record.deliveryMan)?.name ?? record.delivered_by)}</strong>
            </div>
            <div>
              {t('doc.driver')}: <strong>{str(record.driver)}</strong>
            </div>
            <div>
              {t('doc.receivedBy')}: <strong>{str(record.received_by)}</strong>
            </div>
          </div>
        </>
      ) : (
        <p className="vd-cgd">{t('doc.cgd')}</p>
      )}
    </div>
  );
}
